// src/pages/Leaderboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, get } from 'firebase/database';
import logo from '../assets/logo.png';
import { 
  ArrowLeft, Trophy, Target, TrendingUp, Crown, 
  Medal, Award, User, Calendar, Flame, Zap,
  ChevronLeft, ChevronRight, Loader2, WifiOff,
  Sword, Shield, Users, Star, GitBranch, Activity,
  BarChart3, Coffee, Gamepad2, Swords
} from 'lucide-react';

interface PlayerStat {
  uid: string;
  name: string;
  userName: string;
  value: number;
}

interface EsportsPlayer {
  uid: string;
  userName: string;
  matchPlayed: number;
  finishes: number;
}

interface EsportsTeam {
  teamId: string;
  teamName: string;
  matchPlayed: number;
  finishes: number;
  chickenDinner: number;
  totalPoints: number;
}

type GameType = 'BGMI' | 'FFMAX';
type StatType = 'finishes' | 'winnings';
type PeriodType = 'weekly' | 'monthly' | 'yearly' | 'allTime';
type ModeType = 'casual' | 'esports';
type EsportsTabType = 'players' | 'teams';

const Leaderboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [mode, setMode] = useState<ModeType>('casual');
  const [game, setGame] = useState<GameType>('BGMI');
  const [type, setType] = useState<StatType>('finishes');
  const [period, setPeriod] = useState<PeriodType>('allTime');
  const [esportsTab, setEsportsTab] = useState<EsportsTabType>('players');
  const [data, setData] = useState<PlayerStat[]>([]);
  const [esportsPlayers, setEsportsPlayers] = useState<EsportsPlayer[]>([]);
  const [esportsTeams, setEsportsTeams] = useState<EsportsTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [myRank, setMyRank] = useState<number | null>(null);
  const [myValue, setMyValue] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const games: GameType[] = ['BGMI', 'FFMAX'];
  const statTypes: StatType[] = ['finishes', 'winnings'];
  const periods: { key: PeriodType; label: string }[] = [
    { key: 'weekly', label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
    { key: 'yearly', label: 'Yearly' },
    { key: 'allTime', label: 'All Time' },
  ];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate('/login');
      } else {
        setUser(currentUser);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      if (mode === 'casual') {
        loadCasualStats();
        loadMyRank();
      } else {
        loadEsportsStats();
        loadMyEsportsRank();
      }
    }
  }, [game, type, period, mode, esportsTab, user]);

  useEffect(() => {
    const handleOnline = () => setOffline(false);
    const handleOffline = () => setOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadCasualStats = async () => {
    setLoading(true);
    try {
      const db = getDatabase();
      const snapshot = await get(ref(db, `Stats/${game}/${type}/${period}`));

      if (!snapshot.exists()) {
        setData([]);
        setLoading(false);
        return;
      }

      const raw = snapshot.val();
      const list: PlayerStat[] = Object.keys(raw).map((uid) => ({
        uid,
        name: raw[uid].name ?? 'Unknown',
        userName: raw[uid].userName ?? 'unknown',
        value: Number(raw[uid].value ?? 0),
      }));

      list.sort((a, b) => b.value - a.value);
      setData(list.slice(0, 500));
      
      localStorage.setItem(`leaderboard_${game}_${type}_${period}`, JSON.stringify(list.slice(0, 500)));
    } catch (error) {
      console.error('Stats load error:', error);
      setOffline(true);
      const cached = localStorage.getItem(`leaderboard_${game}_${type}_${period}`);
      if (cached) {
        setData(JSON.parse(cached));
      }
    }
    setLoading(false);
  };

  const loadEsportsStats = async () => {
    setLoading(true);
    try {
      const db = getDatabase();
      
      if (esportsTab === 'players') {
        const snapshot = await get(ref(db, `esportsStats/${game}/players`));
        if (snapshot.exists()) {
          const raw = snapshot.val();
          const list: EsportsPlayer[] = Object.keys(raw).map((uid) => ({
            uid,
            userName: raw[uid].userName ?? 'unknown',
            matchPlayed: raw[uid].matchPlayed ?? 0,
            finishes: raw[uid].finishes ?? 0,
          }));
          list.sort((a, b) => b.finishes - a.finishes);
          setEsportsPlayers(list);
        }
      } else {
        const snapshot = await get(ref(db, `esportsStats/${game}/teams`));
        if (snapshot.exists()) {
          const raw = snapshot.val();
          const list: EsportsTeam[] = Object.keys(raw).map((teamId) => ({
            teamId,
            teamName: raw[teamId].teamName ?? 'Unknown',
            matchPlayed: raw[teamId].matchPlayed ?? 0,
            finishes: raw[teamId].finishes ?? 0,
            chickenDinner: raw[teamId].chickenDinner ?? 0,
            totalPoints: raw[teamId].totalPoints ?? 0,
          }));
          list.sort((a, b) => b.totalPoints - a.totalPoints);
          setEsportsTeams(list);
        }
      }
    } catch (error) {
      console.error('Esports stats load error:', error);
    }
    setLoading(false);
  };

const loadMyEsportsRank = async () => {
  if (!user?.uid) return;
  
  try {
    const db = getDatabase();
    
    if (esportsTab === 'players') {
      // For Players: Use user.uid to find rank in esportsStats
      const snapshot = await get(ref(db, `esportsStats/${game}/players`));
      
      if (!snapshot.exists()) {
        setMyRank(null);
        return;
      }
      
      const all = snapshot.val();
      const myData = all[user.uid];
      
      if (!myData) {
        setMyRank(null);
        setMyValue(null);
        return;
      }
      
      // Calculate rank based on finishes
      const playersList = Object.keys(all).map((uid) => ({
        uid,
        finishes: all[uid].finishes ?? 0
      }));
      playersList.sort((a, b) => b.finishes - a.finishes);
      
      const rank = playersList.findIndex(p => p.uid === user.uid) + 1;
      setMyRank(rank);
      setMyValue(myData.finishes);
      
    } else {
      // For Teams: Get user's teamId from Accounts/${user.uid}/myTeam
      const myTeamSnapshot = await get(ref(db, `Accounts/${user.uid}/myTeamId`));
      
      if (!myTeamSnapshot.exists()) {
        setMyRank(null);
        setMyValue(null);
        return;
      }
      
      const myTeamId = myTeamSnapshot.val();
      
      // Then find that team in esportsStats teams
      const teamsSnapshot = await get(ref(db, `esportsStats/${game}/teams`));
      
      if (!teamsSnapshot.exists()) {
        setMyRank(null);
        return;
      }
      
      const allTeams = teamsSnapshot.val();
      const myTeamData = allTeams[myTeamId];
      
      if (!myTeamData) {
        setMyRank(null);
        setMyValue(null);
        return;
      }
      
      // Calculate rank based on totalPoints
      const teamsList = Object.keys(allTeams).map((teamId) => ({
        teamId,
        totalPoints: allTeams[teamId].totalPoints ?? 0
      }));
      teamsList.sort((a, b) => b.totalPoints - a.totalPoints);
      
      const rank = teamsList.findIndex(t => t.teamId === myTeamId) + 1;
      setMyRank(rank);
      setMyValue(myTeamData.totalPoints);
    }
    
  } catch (error) {
    console.error('Error loading my esports rank:', error);
  }
};

  const loadMyRank = async () => {
    if (!user?.uid) return;
    
    try {
      const db = getDatabase();
      const snapshot = await get(ref(db, `Stats/${game}/${type}/${period}`));

      if (!snapshot.exists()) {
        setMyRank(null);
        return;
      }

      const all = snapshot.val();
      const myData = all[user.uid];
      
      if (!myData) {
        setMyRank(null);
        return;
      }

      const myVal = Number(myData.value || 0);
      setMyValue(myVal);

      let higherCount = 0;
      Object.values(all).forEach((u: any) => {
        if (Number(u.value) > myVal) {
          higherCount++;
        }
      });
      setMyRank(higherCount + 1);
    } catch (error) {
      console.error('Rank calc error:', error);
    }
  };

  const getRankColor = (index: number) => {
    if (index === 0) return 'text-yellow-400';
    if (index === 1) return 'text-gray-400';
    if (index === 2) return 'text-amber-600';
    return 'text-gray-500';
  };

  const getRankBgColor = (index: number) => {
    if (index === 0) return 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border-yellow-500/30';
    if (index === 1) return 'bg-gradient-to-r from-gray-500/20 to-gray-600/10 border-gray-500/30';
    if (index === 2) return 'bg-gradient-to-r from-amber-500/20 to-amber-600/10 border-amber-500/30';
    return 'bg-white/5 border-white/10';
  };

  const getMedalIcon = (index: number) => {
    if (index === 0) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (index === 1) return <Medal className="w-6 h-6 text-gray-400" />;
    if (index === 2) return <Medal className="w-6 h-6 text-amber-600" />;
    return null;
  };

  // Pagination
  const totalPages = Math.ceil((mode === 'casual' ? data.length : (esportsTab === 'players' ? esportsPlayers.length : esportsTeams.length)) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCasualData = data.slice(startIndex, startIndex + itemsPerPage);
  const paginatedPlayers = esportsPlayers.slice(startIndex, startIndex + itemsPerPage);
  const paginatedTeams = esportsTeams.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] to-[#0F0F1A] flex items-center justify-center overflow-hidden">
    <div className="absolute w-60 h-60 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
    <div className="absolute w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ top: '20%', left: '10%' }} />
    <div className="absolute w-56 h-56 bg-pink-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ bottom: '20%', right: '10%' }} />
    
    <div className="relative flex flex-col items-center">
      <div className="relative w-28 h-28">
        <div className="absolute inset-0 rounded-full">
          <svg className="w-full h-full animate-spin-slow" viewBox="0 0 100 100">
            <defs>
              <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#A855F7" />
                <stop offset="50%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#EC4899" />
              </linearGradient>
            </defs>
            <circle
              cx="50"
              cy="50"
              r="44"
              fill="none"
              stroke="url(#ringGradient)"
              strokeWidth="3"
              strokeDasharray="200"
              strokeDashoffset="50"
              strokeLinecap="round"
            />
          </svg>
        </div>
        
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-pink-500/20 blur-xl rounded-full animate-pulse" />
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-pink-500/10 rounded-full blur-md animate-pulse-slow" />
        
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src={logo}
            alt="ArenaX"
            className="w-20 h-20 object-contain animate-float"
          />
        </div>
      </div>

      <div className="mt-5 text-center">
        <p className="text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text font-rajdhani font-bold text-base tracking-widest animate-pulse">
          LOADING ARENA...
        </p>
        <div className="flex items-center justify-center gap-1.5 mt-2">
          <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '0s' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0.2s' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-bounce" style={{ animationDelay: '0.4s' }} />
        </div>
      </div>
    </div>
  </div>
  );
}

  return (
    <div className={`min-h-screen transition-all duration-500 ${
      mode === 'esports' 
        ? 'bg-gradient-to-br from-[#1a0a0a] via-[#0a0a0f] to-[#1a0a1a]' 
        : 'bg-gradient-to-br from-[#0A0A0F] to-[#0F0F1A]'
    }`}>
      {/* Header */}
      <div className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        mode === 'esports'
          ? 'bg-gradient-to-r from-red-950/90 to-black/90 border-b border-red-500/20'
          : 'bg-gradient-to-r from-[#0b0f2e] to-[#050816] border-b border-white/10'
      } backdrop-blur-xl`}>
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/dashboard')} 
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-105 ${
                mode === 'esports'
                  ? 'bg-red-500/20 hover:bg-red-500/30 border border-red-500/30'
                  : 'bg-white/5 hover:bg-white/10 border border-white/10'
              }`}
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-white font-rajdhani font-bold text-xl tracking-wider flex items-center gap-2">
                {mode === 'esports' ? (
                  <>
                    <Swords className="w-5 h-5 text-red-500" />
                    ESPORTS ARENA
                  </>
                ) : (
                  <>
                    <Trophy className="w-5 h-5 text-[#00e5ff]" />
                    LEADERBOARD
                  </>
                )}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${mode === 'esports' ? 'bg-red-500' : 'bg-[#00e5ff]'}`} />
                <p className={`text-xs font-rajdhani font-bold ${mode === 'esports' ? 'text-red-400' : 'text-[#00e5ff]'}`}>
                  {mode === 'esports' ? 'PRO CIRCUIT' : 'LIVE UPDATES'}
                </p>
              </div>
            </div>
          </div>

          {/* Mode Toggle */}
          <div className={`flex gap-1 p-1 rounded-xl ${
            mode === 'esports' ? 'bg-red-950/50 border border-red-500/20' : 'bg-white/5 border border-white/10'
          }`}>
            <button
              onClick={() => {
                setMode('casual');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg font-rajdhani font-bold text-sm transition-all flex items-center gap-2 ${
                mode === 'casual'
                  ? 'bg-gradient-to-r from-[#00e5ff] to-[#0066ff] text-white shadow-lg'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Coffee className="w-4 h-4" />
              Casual
            </button>
            <button
              onClick={() => {
                setMode('esports');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg font-rajdhani font-bold text-sm transition-all flex items-center gap-2 ${
                mode === 'esports'
                  ? 'bg-gradient-to-r from-red-600 to-red-800 text-white shadow-lg shadow-red-500/20'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Sword className="w-4 h-4" />
              Esports
            </button>
          </div>
        </div>
      </div>

      <div className="pt-20 pb-8 px-4 max-w-6xl mx-auto">
        {/* Mode Indicator */}
        <div className={`text-center mb-6 ${mode === 'esports' ? 'animate-pulse' : ''}`}>
          {mode === 'esports' ? (
            <>
              <p className="text-red-500 font-rajdhani font-bold text-sm tracking-wider">⚔️ COMPETITIVE MODE ACTIVE ⚔️</p>
              <p className="text-gray-500 text-xs mt-1">Only T2 or Higher Tire Tournaments will Count</p>
            </>
          ) : (
            <>
              <p className="text-[#00e5ff] font-rajdhani font-bold text-sm tracking-wider">🎮 CASUAL MODE</p>
              <p className="text-gray-500 text-xs mt-1">Individual Player Rankings</p>
            </>
          )}
        </div>

        {mode === 'casual' ? (
          <>
            {/* Casual Mode Filters */}
            <div className="bg-[#0b0f2e] rounded-2xl p-4 mb-6 border border-white/10 backdrop-blur-sm">
              {/* Game Tabs */}
              <div className="flex gap-2 mb-4 bg-[#050816] rounded-xl p-1">
                {games.map((g) => (
                  <button
                    key={g}
                    onClick={() => setGame(g)}
                    className={`flex-1 py-2 rounded-lg font-rajdhani font-bold transition-all ${
                      game === g
                        ? 'bg-gradient-to-r from-[#00e5ff] to-[#0066ff] text-white shadow-lg'
                        : 'text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>

              {/* Stat Type Tabs */}
              <div className="flex gap-3 mb-4">
                {statTypes.map((t) => (
                  <button
                    key={t}
                    onClick={() => setType(t)}
                    className={`flex items-center gap-2 px-4 py-1.5 rounded-full transition-all ${
                      type === t
                        ? 'bg-gradient-to-r from-[#00e5ff] to-[#0066ff] text-black shadow-lg'
                        : 'bg-[#111827] text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {t === 'finishes' ? (
                      <Target className="w-4 h-4" />
                    ) : (
                      <Trophy className="w-4 h-4" />
                    )}
                    <span className="font-rajdhani font-bold text-sm">
                      {t.toUpperCase()}
                    </span>
                  </button>
                ))}
              </div>

              {/* Period Filters */}
              <div className="flex flex-wrap gap-2">
                {periods.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => setPeriod(p.key)}
                    className={`px-4 py-1.5 rounded-lg font-rajdhani text-sm transition-all ${
                      period === p.key
                        ? 'bg-[#00e5ff]/20 border border-[#00e5ff] text-[#00e5ff] shadow-lg'
                        : 'bg-white/5 border border-white/10 text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* My Rank Card - Casual */}
            {myRank !== null && !offline && (
              <div className="bg-gradient-to-r from-[#00e5ff]/10 to-[#0066ff]/5 rounded-2xl p-5 mb-6 border border-[#00e5ff]/20 backdrop-blur-sm">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#00e5ff] to-[#0066ff] p-0.5">
                      <div className="w-full h-full rounded-full bg-[#0A0A0F] flex items-center justify-center">
                        <User className="w-6 h-6 text-[#00e5ff]" />
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs font-rajdhani font-bold tracking-wider">YOUR RANK</p>
                      <p className="text-[#00e5ff] text-4xl font-rajdhani font-bold">#{myRank}</p>
                    </div>
                  </div>
                  <div className="w-px h-12 bg-gradient-to-b from-transparent via-[#00e5ff]/30 to-transparent" />
                  <div>
                    <p className="text-gray-400 text-xs font-rajdhani font-bold tracking-wider">
                      {type.toUpperCase()}
                    </p>
                    <p className="text-white text-3xl font-rajdhani font-bold">
                      {myValue?.toLocaleString()}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-[#00e5ff] opacity-50" />
                </div>
              </div>
            )}

            {/* Podium Section - Casual */}
            {data.length >= 3 && (
              <div className="mb-12">
                <div className="flex justify-center items-end gap-4">
                  {/* Second Place */}
                  {data[1] && (
                    <div className="flex-1 max-w-[180px] text-center group">
                      <div className="flex justify-center mb-3">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 p-1 shadow-xl group-hover:scale-105 transition-transform">
                          <div className="w-full h-full rounded-full bg-gradient-to-br from-[#1a1a2e] to-[#0A0A0F] flex items-center justify-center">
                            <span className="text-3xl font-rajdhani font-bold text-gray-300">
                              {data[1].userName[0]?.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-t from-gray-500 to-gray-600 rounded-t-2xl p-3 h-28 flex flex-col justify-end shadow-xl">
                        <p className="text-white font-rajdhani font-bold text-sm truncate">{data[1].userName}</p>
                        <p className="text-white font-rajdhani font-bold text-xl">{data[1].value.toLocaleString()}</p>
                      </div>
                      <p className="text-gray-500 text-xs mt-2 font-rajdhani font-bold">#2</p>
                    </div>
                  )}

                  {/* First Place */}
                  {data[0] && (
                    <div className="flex-1 max-w-[200px] text-center group -mt-8">
                      <div className="flex justify-center mb-3">
                        <div className="relative">
                          <div className="absolute -top-2 -right-2 animate-pulse">
                            <Crown className="w-6 h-6 text-yellow-400" />
                          </div>
                          <div className="w-28 h-28 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 p-1 shadow-2xl group-hover:scale-105 transition-transform">
                            <div className="w-full h-full rounded-full bg-gradient-to-br from-[#1a1a2e] to-[#0A0A0F] flex items-center justify-center">
                              <span className="text-4xl font-rajdhani font-bold text-yellow-400">
                                {data[0].userName[0]?.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-t from-yellow-500 to-orange-500 rounded-t-2xl p-3 h-36 flex flex-col justify-end shadow-2xl">
                        <p className="text-black font-rajdhani font-bold text-base truncate">{data[0].userName}</p>
                        <p className="text-black font-rajdhani font-bold text-2xl">{data[0].value.toLocaleString()}</p>
                      </div>
                      <p className="text-yellow-400 text-xs mt-2 font-rajdhani font-bold">#1 CHAMPION</p>
                    </div>
                  )}

                  {/* Third Place */}
                  {data[2] && (
                    <div className="flex-1 max-w-[180px] text-center group">
                      <div className="flex justify-center mb-3">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-600 to-orange-600 p-1 shadow-xl group-hover:scale-105 transition-transform">
                          <div className="w-full h-full rounded-full bg-gradient-to-br from-[#1a1a2e] to-[#0A0A0F] flex items-center justify-center">
                            <span className="text-3xl font-rajdhani font-bold text-amber-400">
                              {data[2].userName[0]?.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-t from-amber-600 to-orange-600 rounded-t-2xl p-3 h-24 flex flex-col justify-end shadow-xl">
                        <p className="text-white font-rajdhani font-bold text-sm truncate">{data[2].userName}</p>
                        <p className="text-white font-rajdhani font-bold text-xl">{data[2].value.toLocaleString()}</p>
                      </div>
                      <p className="text-gray-500 text-xs mt-2 font-rajdhani font-bold">#3</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Leaderboard List - Casual */}
            <div>
              <h3 className="text-white font-rajdhani font-bold mb-4 flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-500" />
                TOP PERFORMERS
              </h3>
              
              <div className="space-y-2">
                {paginatedCasualData.map((player, idx) => {
                  const globalIndex = startIndex + idx;
                  const isMe = player.uid === user?.uid;
                  
                  return (
                    <div
                      key={player.uid}
                      className={`rounded-xl p-4 border transition-all duration-300 hover:scale-[1.02] ${
                        isMe
                          ? 'bg-gradient-to-r from-[#00e5ff]/20 to-[#0066ff]/10 border-[#00e5ff]/40 shadow-lg shadow-[#00e5ff]/10'
                          : getRankBgColor(globalIndex)
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 text-center">
                          {globalIndex < 3 ? (
                            getMedalIcon(globalIndex)
                          ) : (
                            <span className={`font-rajdhani font-bold text-lg ${getRankColor(globalIndex)}`}>
                              #{globalIndex + 1}
                            </span>
                          )}
                        </div>

                        <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${
                          globalIndex < 3 
                            ? globalIndex === 0 
                              ? 'from-yellow-400 to-orange-500'
                              : globalIndex === 1
                              ? 'from-gray-400 to-gray-500'
                              : 'from-amber-600 to-orange-600'
                            : 'from-[#00e5ff] to-[#0066ff]'
                        } p-0.5`}>
                          <div className="w-full h-full rounded-full bg-[#1a1a2e] flex items-center justify-center">
                            <span className="text-white font-rajdhani font-bold">
                              {player.userName[0]?.toUpperCase()}
                            </span>
                          </div>
                        </div>

                        <div className="flex-1">
                          <p className="text-white font-rajdhani font-bold">
                            {player.userName} {isMe && <span className="text-[#00e5ff] text-xs ml-2">(You)</span>}
                          </p>
                          <p className="text-gray-500 text-xs">{player.name}</p>
                        </div>

                        <div className="text-right">
                          <p className="text-white font-rajdhani font-bold text-xl">
                            {player.value.toLocaleString()}
                          </p>
                          <p className="text-gray-500 text-xs">
                            {type === 'winnings' ? 'Coins' : 'Wins'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Esports Mode */}
            <div className="bg-gradient-to-r from-red-950/30 to-black/30 rounded-2xl p-4 mb-6 border border-red-500/20 backdrop-blur-sm">
              {/* Game Tabs - Esports */}
              <div className="flex gap-2 mb-4 bg-black/50 rounded-xl p-1">
                {games.map((g) => (
                  <button
                    key={g}
                    onClick={() => setGame(g)}
                    className={`flex-1 py-2 rounded-lg font-rajdhani font-bold transition-all ${
                      game === g
                        ? 'bg-gradient-to-r from-red-600 to-red-800 text-white shadow-lg shadow-red-500/20'
                        : 'text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>

              {/* Esports Tabs */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setEsportsTab('players')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-rajdhani font-bold transition-all ${
                    esportsTab === 'players'
                      ? 'bg-gradient-to-r from-red-600 to-red-800 text-white shadow-lg'
                      : 'bg-red-950/30 text-gray-400 border border-red-500/20 hover:text-gray-200'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  Top Players
                </button>
                <button
                  onClick={() => setEsportsTab('teams')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-rajdhani font-bold transition-all ${
                    esportsTab === 'teams'
                      ? 'bg-gradient-to-r from-red-600 to-red-800 text-white shadow-lg'
                      : 'bg-red-950/30 text-gray-400 border border-red-500/20 hover:text-gray-200'
                  }`}
                >
                  <Shield className="w-4 h-4" />
                  Top Teams
                </button>
              </div>
            </div>
{/* Esports Mode - My Rank Card */}
{myRank !== null && !offline && mode === 'esports' && (
  <div className={`rounded-2xl p-5 mb-6 border backdrop-blur-sm ${
    esportsTab === 'players'
      ? 'bg-gradient-to-r from-red-600/20 to-red-800/10 border-red-500/30'
      : 'bg-gradient-to-r from-purple-600/20 to-purple-800/10 border-purple-500/30'
  }`}>
    <div className="flex items-center justify-between flex-wrap gap-4">
      <div className="flex items-center gap-4">
        <div className={`w-14 h-14 rounded-full p-0.5 ${
          esportsTab === 'players'
            ? 'bg-gradient-to-r from-red-500 to-red-700'
            : 'bg-gradient-to-r from-purple-500 to-purple-700'
        }`}>
          <div className="w-full h-full rounded-full bg-[#0A0A0F] flex items-center justify-center">
            {esportsTab === 'players' ? (
              <User className="w-6 h-6 text-red-400" />
            ) : (
              <Shield className="w-6 h-6 text-purple-400" />
            )}
          </div>
        </div>
        <div>
          <p className="text-gray-400 text-xs font-rajdhani font-bold tracking-wider">
            {esportsTab === 'players' ? 'YOUR PLAYER RANK' : 'YOUR TEAM RANK'}
          </p>
          <p className={`text-4xl font-rajdhani font-bold ${
            esportsTab === 'players' ? 'text-red-500' : 'text-purple-400'
          }`}>
            #{myRank}
          </p>
        </div>
      </div>
      <div className="w-px h-12 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
      <div>
        <p className="text-gray-400 text-xs font-rajdhani font-bold tracking-wider">
          {esportsTab === 'players' ? 'TOTAL FINISHES' : 'TOTAL POINTS'}
        </p>
        <p className="text-white text-3xl font-rajdhani font-bold">
          {myValue?.toLocaleString()}
        </p>
      </div>
      <TrendingUp className={`w-8 h-8 opacity-50 ${
        esportsTab === 'players' ? 'text-red-500' : 'text-purple-400'
      }`} />
    </div>
  </div>
)}
            {/* Esports Content */}
            <div>
              <h3 className="text-white font-rajdhani font-bold mb-4 flex items-center gap-2">
                <Sword className="w-4 h-4 text-red-500" />
                {esportsTab === 'players' ? 'PRO PLAYERS RANKINGS' : 'PRO TEAMS RANKINGS'}
              </h3>

              <div className="space-y-3">
                {esportsTab === 'players' ? (
                  paginatedPlayers.map((player, idx) => {
                    const globalIndex = startIndex + idx;
                    return (
                      <div
                        key={player.uid}
                        className="bg-gradient-to-r from-red-950/20 to-black/20 rounded-xl p-4 border border-red-500/20 hover:border-red-500/40 transition-all duration-300 hover:scale-[1.01]"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 text-center">
                            {globalIndex < 3 ? (
                              globalIndex === 0 ? (
                                <Crown className="w-6 h-6 text-yellow-400" />
                              ) : globalIndex === 1 ? (
                                <Medal className="w-6 h-6 text-gray-400" />
                              ) : (
                                <Medal className="w-6 h-6 text-amber-600" />
                              )
                            ) : (
                              <span className="font-rajdhani font-bold text-lg text-gray-400">
                                #{globalIndex + 1}
                              </span>
                            )}
                          </div>

                          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-red-600 to-red-800 p-0.5">
                            <div className="w-full h-full rounded-full bg-[#1a1a2e] flex items-center justify-center">
                              <span className="text-white font-rajdhani font-bold text-lg">
                                {player.userName[0]?.toUpperCase()}
                              </span>
                            </div>
                          </div>

                          <div className="flex-1">
                            <p className="text-white font-rajdhani font-bold text-lg">{player.userName}</p>
                            <div className="flex gap-4 mt-1">
                              <span className="text-gray-400 text-xs flex items-center gap-1">
                                <Gamepad2 className="w-3 h-3" /> {player.matchPlayed} Matches
                              </span>
                              <span className="text-green-400 text-xs flex items-center gap-1">
                                <Target className="w-3 h-3" /> {(player.finishes / player.matchPlayed).toFixed(2)} FD
                              </span>
                            </div>
                          </div>

                          <div className="text-right">
                            <p className="text-red-400 font-rajdhani font-bold text-xl">
                              {player.finishes}
                            </p>
                            <p className="text-gray-500 text-xs">Finishes</p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  paginatedTeams.map((team, idx) => {
                    const globalIndex = startIndex + idx;
                    return (
                      <div
                        key={team.teamId}
                        className="bg-gradient-to-r from-red-950/20 to-black/20 rounded-xl p-4 border border-red-500/20 hover:border-red-500/40 transition-all duration-300 hover:scale-[1.01]"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 text-center">
                            {globalIndex < 3 ? (
                              globalIndex === 0 ? (
                                <Crown className="w-6 h-6 text-yellow-400" />
                              ) : globalIndex === 1 ? (
                                <Medal className="w-6 h-6 text-gray-400" />
                              ) : (
                                <Medal className="w-6 h-6 text-amber-600" />
                              )
                            ) : (
                              <span className="font-rajdhani font-bold text-lg text-gray-400">
                                #{globalIndex + 1}
                              </span>
                            )}
                          </div>

                          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-red-600 to-red-800 p-0.5">
                            <div className="w-full h-full rounded-full bg-[#1a1a2e] flex items-center justify-center">
                              <Shield className="w-6 h-6 text-red-400" />
                            </div>
                          </div>

                          <div className="flex-1">
                            <p className="text-white font-rajdhani font-bold text-lg">{team.teamName}</p>
                            <div className="flex gap-3 mt-1 flex-wrap">
                              <span className="text-gray-400 text-xs flex items-center gap-1">
                                <Gamepad2 className="w-3 h-3" /> {team.matchPlayed} Matches
                              </span>
                              <span className="text-green-400 text-xs flex items-center gap-1">
                                <Sword className="w-3 h-3" /> {team.finishes} Finishes
                              </span>
                              <span className="text-yellow-400 text-xs flex items-center gap-1">
                                <Star className="w-3 h-3" /> {team.chickenDinner} CD
                              </span>
                            </div>
                          </div>

                          <div className="text-right">
                            <p className="text-red-400 font-rajdhani font-bold text-xl">
                              {team.totalPoints}
                            </p>
                            <p className="text-gray-500 text-xs">Total Points</p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                mode === 'esports'
                  ? 'bg-red-950/50 border border-red-500/20 disabled:opacity-50 hover:bg-red-500/20'
                  : 'bg-white/5 border border-white/10 disabled:opacity-50 hover:bg-white/10'
              }`}
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <div className="flex gap-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-10 h-10 rounded-lg font-rajdhani font-bold transition-all ${
                      currentPage === pageNum
                        ? mode === 'esports'
                          ? 'bg-gradient-to-r from-red-600 to-red-800 text-white shadow-lg'
                          : 'bg-gradient-to-r from-[#00e5ff] to-[#0066ff] text-black shadow-lg'
                        : mode === 'esports'
                        ? 'bg-red-950/30 border border-red-500/20 text-white hover:bg-red-500/20'
                        : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                mode === 'esports'
                  ? 'bg-red-950/50 border border-red-500/20 disabled:opacity-50 hover:bg-red-500/20'
                  : 'bg-white/5 border border-white/10 disabled:opacity-50 hover:bg-white/10'
              }`}
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && mode === 'casual' && data.length === 0 && (
          <div className="text-center py-12">
            <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 font-rajdhani">No data available for this period</p>
          </div>
        )}
        {!loading && mode === 'esports' && esportsTab === 'players' && esportsPlayers.length === 0 && (
          <div className="text-center py-12">
            <Sword className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 font-rajdhani">No esports players data available</p>
          </div>
        )}
        {!loading && mode === 'esports' && esportsTab === 'teams' && esportsTeams.length === 0 && (
          <div className="text-center py-12">
            <Shield className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 font-rajdhani">No esports teams data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;